import { useEffect, useRef, useState } from 'react';
import { api, ErreurApi } from '../api.js';
import { tokens, couleursImpact, couleursUrgence, couleursType, champsDescription } from '../tokens.js';
import {
  Badge, BoiteErreur, labelChamp, inputBase,
  boutonPrincipal, boutonSecondaire, boutonDanger,
} from '../helpers.jsx';
import {
  ArrowLeft, Send, Paperclip, X, MessageCircleQuestion, AlertTriangle, ChevronDown,
} from 'lucide-react';

const MAX_FICHIERS = 5;
const TAILLE_MAX_FICHIER = 5 * 1024 * 1024;
const IMPACTS  = ['bloquant', 'contournement', 'agacant', 'cosmetique'];

// Formulaire de creation structure :
// 1. Catégorisation (service + sous-service + impact + urgence)
// 2. Description (3 champs)
// 3. Pièces jointes
// 4. Submit -> analyse IA -> ticket ou clarif
// 5. Si clarif IA echoue 2x -> bouton "envoyer quand meme" avec double friction
export function CreerTicket({ onRetour, onCree }) {
  // Etat formulaire
  const [type, setType]             = useState('bug');
  const [services, setServices]     = useState([]);
  const [serviceId, setServiceId]   = useState('');
  const [sousServiceId, setSousId]  = useState('');
  const [impact, setImpact]         = useState('');
  const [urgence, setUrgence]       = useState('peut_attendre');
  const [dAction, setDAction]       = useState('');
  const [dObserv, setDObserv]       = useState('');
  const [dAttendu, setDAttendu]     = useState('');
  const [fichiers, setFichiers]     = useState([]);
  // Etat flow
  const [envoiEnCours, setEnvoi]    = useState(false);
  const [erreur, setErreur]         = useState('');
  const [tokenTicket, setTokenTicket] = useState(null);
  const [questionsClarif, setQClarif] = useState([]);
  const [reponsesClarif, setReponses] = useState({});
  const [forceModalOuverte, setForceOuvert] = useState(false);
  const [passesClarif, setPasses]   = useState(0);

  const inputFichierRef = useRef(null);

  // Chargement des services au mount
  useEffect(() => {
    api.get('/services')
      .then(d => setServices(d.services || []))
      .catch(e => setErreur(e.message || 'Impossible de charger les services'));
  }, []);

  const serviceChoisi = services.find(s => String(s.id) === String(serviceId));
  const sousServices  = serviceChoisi?.sous_services || [];

  // Helpers fichiers
  function ajouterFichiers(liste) {
    const nouveaux = Array.from(liste).filter(f => {
      if (!f.type.startsWith('image/')) {
        setErreur(`"${f.name}" n'est pas une image (PNG/JPG/WEBP).`);
        return false;
      }
      if (f.size > TAILLE_MAX_FICHIER) {
        setErreur(`"${f.name}" depasse 5 Mo.`);
        return false;
      }
      return true;
    });
    setFichiers(prev => [...prev, ...nouveaux].slice(0, MAX_FICHIERS));
  }

  function retirerFichier(idx) {
    setFichiers(prev => prev.filter((_, i) => i !== idx));
  }

  // Validation
  function valider() {
    if (!serviceId)  return 'Choisis un service';
    if (sousServices.length > 0 && !sousServiceId) return 'Choisis un sous-service';
    if (!impact)     return 'Choisis l\'impact';
    if (!urgence)    return 'Choisis l\'urgence';
    if (dAction.trim().length  < 5) return 'Précise ce que tu faisais (5 caractères min)';
    if (dObserv.trim().length  < 5) return 'Précise ce qui s\'est passé (5 caractères min)';
    if (dAttendu.trim().length < 5) return 'Précise ce à quoi tu t\'attendais (5 caractères min)';
    return null;
  }

  // Submit initial
  async function soumettre(e) {
    e?.preventDefault();
    const v = valider();
    if (v) { setErreur(v); return; }
    setErreur(''); setEnvoi(true);
    try {
      const idServiceFinal = sousServiceId || serviceId;
      const fd = new FormData();
      fd.append('type', type);
      fd.append('service_id', idServiceFinal);
      fd.append('impact', impact);
      fd.append('urgence', urgence);
      fd.append('description_action',      dAction.trim());
      fd.append('description_observation', dObserv.trim());
      fd.append('description_attendu',     dAttendu.trim());
      fichiers.forEach(f => fd.append('fichiers[]', f));

      const rep = await api.postFormData('/tickets', fd);
      const token = rep.ticket?.token_public;
      if (rep.besoin_clarif && rep.questions_clarif?.length) {
        setTokenTicket(token);
        setQClarif(rep.questions_clarif);
        setReponses(Object.fromEntries(rep.questions_clarif.map(q => [q, ''])));
        setPasses(1);
      } else if (token) {
        onCree(token);
      }
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : 'Erreur d\'envoi');
    } finally {
      setEnvoi(false);
    }
  }

  // Submit clarif
  async function soumettreClarif(e) {
    e.preventDefault();
    setErreur('');
    const reponses = {};
    for (const [q, r] of Object.entries(reponsesClarif)) {
      if (r.trim().length >= 3) reponses[q] = r.trim();
    }
    if (Object.keys(reponses).length === 0) {
      setErreur('Réponds à au moins une question (3 caractères min)');
      return;
    }
    setEnvoi(true);
    try {
      const rep = await api.post(`/tickets/${tokenTicket}/clarif`, { reponses });
      if (rep.besoin_clarif && rep.questions_clarif?.length) {
        setQClarif(rep.questions_clarif);
        setReponses(Object.fromEntries(rep.questions_clarif.map(q => [q, ''])));
        setPasses(p => p + 1);
      } else {
        onCree(tokenTicket);
      }
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : 'Erreur d\'envoi');
    } finally {
      setEnvoi(false);
    }
  }

  // ----- Vue clarification IA -----
  if (tokenTicket && questionsClarif.length > 0) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        <BoutonRetour onClic={onRetour} />
        <div style={{
          background: tokens.warningBg,
          border: `1px solid ${tokens.warning}40`,
          color: tokens.warning,
          padding: '14px 18px', borderRadius: 8,
          display:'flex', alignItems:'flex-start', gap: 10,
          marginBottom: 18,
        }}>
          <MessageCircleQuestion size={20} style={{ flexShrink:0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              On a besoin de précisions
            </div>
            <div style={{ fontSize: 13, opacity: 0.85 }}>
              Ton ticket <strong>#{tokenTicket}</strong> est créé mais il manque
              du contexte pour qu'on puisse agir dessus. Réponds aux questions ci-dessous.
            </div>
          </div>
        </div>

        <form onSubmit={soumettreClarif} style={cadreForm}>
          {questionsClarif.map((q, i) => (
            <div key={i} style={{ marginBottom: 18 }}>
              <label style={labelChamp}>{q}</label>
              <textarea
                rows={2}
                value={reponsesClarif[q] || ''}
                onChange={(e) => setReponses(r => ({ ...r, [q]: e.target.value }))}
                style={{ ...inputBase, resize: 'vertical' }}
              />
            </div>
          ))}

          {erreur && <BoiteErreur message={erreur} />}

          <div style={{ display:'flex', gap: 10, marginTop: 8, flexWrap:'wrap' }}>
            <button type="submit" disabled={envoiEnCours} style={boutonPrincipal}>
              <Send size={14} /> {envoiEnCours ? 'Analyse IA…' : 'Compléter'}
            </button>
            {passesClarif >= 2 && (
              <button
                type="button"
                onClick={() => setForceOuvert(true)}
                style={{
                  ...boutonSecondaire,
                  color: tokens.danger, borderColor: tokens.dangerBg,
                }}
              >
                <AlertTriangle size={14} /> Envoyer quand même
              </button>
            )}
          </div>
          {passesClarif < 2 && (
            <p style={{ marginTop: 10, fontSize: 11, color: tokens.inkMuted }}>
              Si la 2<sup>ème</sup> passe IA reste insuffisante, une option d'envoi forcé apparaîtra (déconseillée).
            </p>
          )}
        </form>

        {forceModalOuverte && (
          <ModaleEnvoiForce
            tokenTicket={tokenTicket}
            onAnnuler={() => setForceOuvert(false)}
            onValide={() => onCree(tokenTicket)}
          />
        )}
      </div>
    );
  }

  // ----- Formulaire initial -----
  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <BoutonRetour onClic={onRetour} />

      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 600, color: tokens.ink }}>
        Nouveau ticket
      </h2>
      <p style={{ margin: '0 0 22px', fontSize: 13, color: tokens.inkSoft }}>
        Catégorise précisément, puis décris en 3 temps : ce que tu faisais,
        ce qui s'est passé, et ce à quoi tu t'attendais.
      </p>

      <form onSubmit={soumettre}>
        {/* SECTION 0 : Type */}
        <section style={cadreForm}>
          <h3 style={titreSection}>Type de signalement *</h3>
          <div style={{ display:'flex', gap: 10 }}>
            {['bug', 'evolution'].map(t => {
              const meta = couleursType[t];
              return (
                <label
                  key={t}
                  style={{
                    flex: 1, display:'flex', flexDirection:'column', gap: 4,
                    padding: '12px 14px',
                    border: `1px solid ${type === t ? tokens.accent : tokens.border}`,
                    background: type === t ? tokens.accentBg : tokens.surface,
                    borderRadius: 8, cursor:'pointer',
                  }}
                >
                  <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
                    <input
                      type="radio" name="type" value={t}
                      checked={type === t}
                      onChange={() => setType(t)}
                    />
                    <span style={{ fontSize: 13, fontWeight: 600, color: tokens.ink }}>
                      {meta.titre}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: tokens.inkSoft, marginLeft: 22 }}>
                    {meta.hint}
                  </span>
                </label>
              );
            })}
          </div>
        </section>

        {/* SECTION 1 : Catégorisation */}
        <section style={{ ...cadreForm, marginTop: 14 }}>
          <h3 style={titreSection}>Contexte</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelChamp}>Service *</label>
              <Select
                value={serviceId}
                onChange={(v) => { setServiceId(v); setSousId(''); }}
                placeholder="— Choisis un service —"
                options={services.map(s => ({ value: s.id, label: s.nom }))}
              />
            </div>
            <div>
              <label style={labelChamp}>
                Sous-service {sousServices.length > 0 ? '*' : '(aucun)'}
              </label>
              <Select
                value={sousServiceId}
                onChange={setSousId}
                placeholder={sousServices.length === 0 ? '—' : '— Choisis —'}
                disabled={sousServices.length === 0}
                options={sousServices.map(s => ({ value: s.id, label: s.nom }))}
              />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelChamp}>En quoi ça te bloque ? *</label>
            <div style={{ display: 'flex', flexDirection:'column', gap: 8 }}>
              {IMPACTS.map(imp => {
                const meta = couleursImpact[imp];
                return (
                  <label
                    key={imp}
                    style={{
                      display:'flex', alignItems:'flex-start', gap: 10,
                      padding: '10px 12px',
                      border: `1px solid ${impact === imp ? tokens.accent : tokens.border}`,
                      background: impact === imp ? tokens.accentBg : tokens.surface,
                      borderRadius: 8, cursor:'pointer',
                    }}
                  >
                    <input
                      type="radio" name="impact" value={imp}
                      checked={impact === imp}
                      onChange={() => setImpact(imp)}
                      style={{ marginTop: 2 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: tokens.ink }}>
                        {meta.label}
                      </div>
                      <div style={{ fontSize: 11, color: tokens.inkSoft }}>
                        {meta.hint}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label style={labelChamp}>Urgence *</label>
            <div style={{ display:'flex', gap: 8 }}>
              {['urgent', 'peut_attendre'].map(u => {
                const meta = couleursUrgence[u];
                return (
                  <label
                    key={u}
                    style={{
                      flex: 1, display:'flex', alignItems:'center', gap: 8,
                      padding: '10px 12px',
                      border: `1px solid ${urgence === u ? tokens.accent : tokens.border}`,
                      background: urgence === u ? tokens.accentBg : tokens.surface,
                      borderRadius: 8, cursor:'pointer',
                    }}
                  >
                    <input
                      type="radio" name="urgence" value={u}
                      checked={urgence === u}
                      onChange={() => setUrgence(u)}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{meta.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 2 : Description (libellés dynamiques selon le type) */}
        <section style={{ ...cadreForm, marginTop: 14 }}>
          <h3 style={titreSection}>Description</h3>

          <div style={{ marginBottom: 14 }}>
            <label style={labelChamp}>{champsDescription[type].action.label} *</label>
            <textarea
              rows={2}
              value={dAction}
              onChange={(e) => setDAction(e.target.value)}
              placeholder={champsDescription[type].action.placeholder}
              maxLength={2000}
              style={{ ...inputBase, resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelChamp}>{champsDescription[type].observation.label} *</label>
            <textarea
              rows={3}
              value={dObserv}
              onChange={(e) => setDObserv(e.target.value)}
              placeholder={champsDescription[type].observation.placeholder}
              maxLength={2000}
              style={{ ...inputBase, resize: 'vertical' }}
            />
          </div>
          <div>
            <label style={labelChamp}>{champsDescription[type].attendu.label} *</label>
            <textarea
              rows={2}
              value={dAttendu}
              onChange={(e) => setDAttendu(e.target.value)}
              placeholder={champsDescription[type].attendu.placeholder}
              maxLength={2000}
              style={{ ...inputBase, resize: 'vertical' }}
            />
          </div>
        </section>

        {/* SECTION 3 : Pièces jointes */}
        <section style={{ ...cadreForm, marginTop: 14 }}>
          <h3 style={titreSection}>Pièces jointes (optionnel)</h3>
          <input
            ref={inputFichierRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            onChange={(e) => { ajouterFichiers(e.target.files); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => inputFichierRef.current?.click()}
            style={{ ...boutonSecondaire, fontSize: 13 }}
            disabled={fichiers.length >= MAX_FICHIERS}
          >
            <Paperclip size={14} /> Joindre une capture
          </button>
          <span style={{ marginLeft: 10, fontSize: 11, color: tokens.inkMuted }}>
            {fichiers.length}/{MAX_FICHIERS} fichiers · images uniquement · 5 Mo max
          </span>

          {fichiers.length > 0 && (
            <div style={{ marginTop: 10, display:'flex', flexWrap:'wrap', gap: 8 }}>
              {fichiers.map((f, i) => (
                <div key={i} style={{
                  display:'flex', alignItems:'center', gap: 6,
                  background: tokens.surfaceAlt,
                  padding: '4px 8px', borderRadius: 6, fontSize: 12,
                }}>
                  <Paperclip size={12} color={tokens.inkSoft} />
                  <span style={{ color: tokens.inkSoft }}>{f.name}</span>
                  <button
                    type="button"
                    onClick={() => retirerFichier(i)}
                    style={{ background:'transparent', border:'none', color: tokens.inkMuted, cursor:'pointer' }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {erreur && <BoiteErreur message={erreur} />}

        <button
          type="submit"
          disabled={envoiEnCours}
          style={{ ...boutonPrincipal, marginTop: 18 }}
        >
          <Send size={14} /> {envoiEnCours ? 'Analyse IA…' : 'Envoyer le ticket'}
        </button>
      </form>
    </div>
  );
}

// Modale d'envoi force (double friction)
function ModaleEnvoiForce({ tokenTicket, onAnnuler, onValide }) {
  const [coche1, setCoche1] = useState(false);
  const [coche2, setCoche2] = useState(false);
  const [justif, setJustif] = useState('');
  const [envoi, setEnvoi]   = useState(false);
  const [erreur, setErreur] = useState('');

  const valide = coche1 && coche2 && justif.trim().length >= 20;

  async function confirmer() {
    if (!valide) return;
    setErreur(''); setEnvoi(true);
    try {
      await api.post(`/tickets/${tokenTicket}/envoi-force`, { justification: justif.trim() });
      onValide();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    } finally { setEnvoi(false); }
  }

  return (
    <div style={{
      position:'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding: 20,
    }}>
      <div style={{
        background: tokens.surface,
        borderRadius: 12,
        maxWidth: 480, width: '100%',
        padding: 28,
        boxShadow: '0 10px 40px rgba(0,0,0,0.20)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 14, color: tokens.danger }}>
          <AlertTriangle size={22} />
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Envoi forcé du ticket</h3>
        </div>

        <p style={{ fontSize: 13, color: tokens.inkSoft, lineHeight: 1.5, marginTop: 0 }}>
          L'analyse IA estime que ton ticket ne contient pas assez d'informations
          pour qu'un dev puisse agir. Si tu envoies quand même, il y a un risque
          élevé qu'il soit <strong>refusé</strong> par l'admin sans traitement.
        </p>

        <label style={cocheStyle}>
          <input type="checkbox" checked={coche1} onChange={(e) => setCoche1(e.target.checked)} />
          Je confirme que je ne peux pas mieux décrire le problème.
        </label>
        <label style={cocheStyle}>
          <input type="checkbox" checked={coche2} onChange={(e) => setCoche2(e.target.checked)} />
          J'accepte que mon ticket soit possiblement refusé sans suivi.
        </label>

        <label style={{ ...labelChamp, marginTop: 14 }}>
          Pourquoi tu ne peux pas mieux décrire ? *
          <span style={{ color: tokens.inkMuted, fontWeight: 400, marginLeft: 6 }}>
            (20 caractères minimum)
          </span>
        </label>
        <textarea
          rows={3}
          value={justif}
          onChange={(e) => setJustif(e.target.value)}
          placeholder="Ex : le bug est intermittent, je n'arrive pas à le reproduire à la demande"
          style={{ ...inputBase, resize: 'vertical' }}
        />
        <div style={{ fontSize: 11, color: tokens.inkMuted, textAlign:'right', marginTop: 2 }}>
          {justif.length} / 20
        </div>

        {erreur && <BoiteErreur message={erreur} />}

        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 18 }}>
          <button onClick={onAnnuler} style={boutonSecondaire}>Annuler</button>
          <button
            onClick={confirmer}
            disabled={!valide || envoi}
            style={{ ...boutonDanger, opacity: valide && !envoi ? 1 : 0.5 }}
          >
            {envoi ? 'Envoi…' : 'Envoyer quand même'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Composants utilitaires

function Select({ value, onChange, placeholder, options, disabled = false }) {
  return (
    <div style={{ position:'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          ...inputBase,
          appearance: 'none',
          paddingRight: 32,
          background: disabled ? tokens.bg : tokens.surface,
          color: !value ? tokens.inkMuted : tokens.ink,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value} style={{ color: tokens.ink }}>{o.label}</option>
        ))}
      </select>
      <ChevronDown
        size={14}
        color={tokens.inkMuted}
        style={{ position:'absolute', right: 10, top: 13, pointerEvents:'none' }}
      />
    </div>
  );
}

function BoutonRetour({ onClic }) {
  return (
    <button
      onClick={onClic}
      style={{
        display:'inline-flex', alignItems:'center', gap: 4,
        background:'transparent', border:'none', cursor:'pointer',
        color: tokens.inkSoft, fontSize: 13,
        marginBottom: 16, padding: 0,
      }}
    >
      <ArrowLeft size={14} /> Retour
    </button>
  );
}

// Styles
const cadreForm = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 22,
};

const titreSection = {
  margin: '0 0 16px',
  fontSize: 14,
  fontWeight: 600,
  color: tokens.inkSoft,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const cocheStyle = {
  display:'flex', alignItems:'flex-start', gap: 10,
  marginTop: 12,
  fontSize: 13, color: tokens.ink,
  cursor:'pointer', lineHeight: 1.5,
};
